/**
 * Provider alias → canonical config key. PURE: no node APIs, no secrets, no
 * network — safe in a browser bundle.
 *
 * Why it lives here rather than in an app: a connection's `provider` is reported
 * as the provider TEMPLATE slug by older rows (Outlook → "outlook") while every
 * dispatch path keys on the integration CONFIG key (Outlook → "outlook-oauth").
 * The two are NOT equal for Outlook, and they happen to coincide for Gmail —
 * which is exactly why a divergent copy of this map goes unnoticed until an
 * Outlook mailbox silently stops matching. One map, one normalizer.
 */

/** Canonical integration config keys this package knows how to normalize onto. */
export const CONNECTION_PROVIDER_CONFIG_KEYS = [
  "google-mail",
  "outlook-oauth",
  "slack-oauth",
  "professional-email",
] as const;

export type ConnectionProviderConfigKey = (typeof CONNECTION_PROVIDER_CONFIG_KEYS)[number];

/**
 * Every alias we may receive — legacy template slug, legacy UI key, or the
 * canonical config key itself — mapped to its canonical config key.
 */
const PROVIDER_ALIAS_TO_CONFIG_KEY: Record<string, ConnectionProviderConfigKey> = {
  // Gmail — the legacy template slug already equals the config key.
  gmail: "google-mail",
  "google-mail": "google-mail",
  // Outlook — legacy rows carry the template slug "outlook"; the config key is "outlook-oauth".
  outlook: "outlook-oauth",
  "outlook-oauth": "outlook-oauth",
  // Slack (not email, but shares the connection namespace).
  slack: "slack-oauth",
  "slack-oauth": "slack-oauth",
  // WTC internal mail bridge (no OAuth connection; injected by the connection list).
  "professional-email": "professional-email",
};

/**
 * Normalize any provider alias to its canonical config key. An unrecognized
 * value is returned UNCHANGED, so an unknown provider surfaces as itself rather
 * than being silently remapped onto a mailbox it is not.
 */
export function normalizeProviderAlias(provider: string): string {
  // Lower-cased because the aliases arrive from three sources that disagree
  // about case: a stored row, a tool schema, and whatever the model typed.
  const trimmed = provider.trim().toLowerCase();
  return PROVIDER_ALIAS_TO_CONFIG_KEY[trimmed] ?? trimmed;
}

/** True when `value` is one of the canonical config keys. */
export function isConnectionProviderConfigKey(
  value: string
): value is ConnectionProviderConfigKey {
  return (CONNECTION_PROVIDER_CONFIG_KEYS as readonly string[]).includes(value);
}
